import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { PageContainer } from '../../components/layout/PageContainer/PageContainer';
import { Toggle } from '../../components/ui/Toggle/Toggle';
import { useSecretTap } from '../../components/technician/useSecretTap';
import { ROUTE } from '../../lib/rcu/catalog/navigation';
import { heroImages } from '../../data/mockData';

export const SettingsPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [notifications, setNotifications] = React.useState(true);
  const [darkMode, setDarkMode] = React.useState(false);
  const [autoLock, setAutoLock] = React.useState(true);

  // Hidden entrance to the technician console: five quick taps on the logo.
  const handleLogoTap = useSecretTap(() => navigate(ROUTE.TECHNICIAN));

  return (
    <PageContainer
      heroImage={heroImages.settings}
      heroHeight="small"
      heroTitle={t('settings.title')}
      heroSubtitle={t('settings.subtitle')}
      overlayOpacity={0.45}
    >
      {/* Settings Content */}
      <div className="bg-white rounded-2xl p-6 shadow-[0_2px_8px_rgba(0,0,0,0.08)]">
        <h2 className="font-display text-2xl text-[#333333] mb-6">Preferences</h2>

        {/* Settings List */}
        <div className="space-y-6">
          {/* Notifications */}
          <div className="flex items-center justify-between py-4 border-b border-[#E0E0E0]">
            <div>
              <h3 className="text-[#333333] font-medium mb-1">Notifications</h3>
              <p className="text-[#666666] text-sm">Receive updates about your stay</p>
            </div>
            <Toggle
              checked={notifications}
              onChange={setNotifications}
            />
          </div>

          {/* Dark Mode */}
          <div className="flex items-center justify-between py-4 border-b border-[#E0E0E0]">
            <div>
              <h3 className="text-[#333333] font-medium mb-1">Dark Mode</h3>
              <p className="text-[#666666] text-sm">Switch to dark theme</p>
            </div>
            <Toggle
              checked={darkMode}
              onChange={setDarkMode}
            />
          </div>

          {/* Auto Lock */}
          <div className="flex items-center justify-between py-4 border-b border-[#E0E0E0]">
            <div>
              <h3 className="text-[#333333] font-medium mb-1">Auto Lock</h3>
              <p className="text-[#666666] text-sm">Lock screen after inactivity</p>
            </div>
            <Toggle
              checked={autoLock}
              onChange={setAutoLock}
            />
          </div>

          {/* Language Selector */}
          <div className="py-4 border-b border-[#E0E0E0]">
            <label className="block text-[#333333] font-medium mb-2">Language</label>
            <select className="w-full h-12 px-4 bg-[#F5F5F5] rounded-xl text-[#333333] focus:outline-none focus:ring-2 focus:ring-[#D4A054]">
              <option value="en">English</option>
              <option value="hi">Hindi</option>
              <option value="fr">French</option>
              <option value="es">Spanish</option>
            </select>
          </div>

          {/* Temperature Unit */}
          <div className="py-4">
            <label className="block text-[#333333] font-medium mb-2">Temperature Unit</label>
            <div className="flex gap-4">
              <button className="flex-1 h-12 px-4 bg-[#D4A054] text-white rounded-xl font-medium">
                Celsius
              </button>
              <button className="flex-1 h-12 px-4 bg-[#F5F5F5] text-[#666666] rounded-xl font-medium hover:bg-[#E0E0E0]">
                Fahrenheit
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Footer Section */}
      <div className="mt-12 text-center">
        <div className="flex flex-col items-center gap-3">
          {/* Toyama Logo - text sized. Also the hidden technician entrance. */}
          <button
            type="button"
            onClick={handleLogoTap}
            aria-label="Toyama"
            className="cursor-default focus:outline-none"
          >
            <img
              src={`${import.meta.env.BASE_URL}toyama_logo.svg`}
              alt="Toyama"
              className="h-4 opacity-60"
            />
          </button>
          
          {/* Made with love */}
          <p className="text-[#999999] text-xs">
            Made with <span className="text-[#D4A054]">♥</span> in Bangalore, India
          </p>
        </div>
      </div>
    </PageContainer>
  );
};

export default SettingsPage;